import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { login } from '../../hooks/auth';
import useAuth from '../../hooks/useAuth';
import { FaUser, FaLock, FaSpinner } from 'react-icons/fa';

const Authentication = () => {
    const { user, userLoading } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleLogin = async (e) => {
        e.preventDefault();
        
        // Validation
        if (!email.trim() || !password.trim()) {
            toast.error('Veuillez remplir tous les champs');
            return;
        }

        setLoading(true);

        try {
            const { token, user } = await login(email, password);
            
            if (token) {
                localStorage.setItem('token', token);
                toast.success('Connexion réussie !');
                
                // Small delay to show success message before navigation
                setTimeout(() => {
                    navigate('/admin/dashboard');
                }, 500);
            }
            
        } catch (error) {
            console.error('Login error:', error);
            
            // Handle different error scenarios
            if (error.response) {
                const status = error.response.status;
                const message = error.response.data?.message;
                
                if (status === 401) {
                    if (message?.includes('not found') || message?.includes('User not found')) {
                        toast.error('Utilisateur introuvable. Vérifiez vos identifiants.');
                    } else if (message?.includes('password') || message?.includes('Wrong')) {
                        toast.error('Mot de passe incorrect. Veuillez réessayer.');
                    } else {
                        toast.error('Identifiants incorrects. Veuillez vérifier votre email/username et mot de passe.');
                    }
                } else if (status === 500) {
                    toast.error('Erreur serveur. Veuillez réessayer plus tard.');
                } else {
                    toast.error(message || 'Échec de la connexion. Veuillez réessayer.');
                }
            } else if (error.request) {
                toast.error('Impossible de contacter le serveur. Vérifiez votre connexion internet.');
            } else {
                toast.error('Une erreur est survenue. Veuillez réessayer.');
            }
        } finally {
            setLoading(false);
        }
    };

    if (userLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <FaSpinner className="animate-spin text-primary text-4xl mx-auto mb-4" />
                    <p className="text-gray-600">Chargement...</p>
                </div>
            </div>
        );
    }
    
    if (user) {
        return <Navigate to="/admin/dashboard" />;
    }

    return (
        <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 via-white to-gray-50 px-4 py-12">
            <div className="w-full max-w-md">
                {/* Card Container */}
                <div className="bg-white rounded-2xl shadow-xl p-8 md:p-10">
                    {/* Header */}
                    <div className="text-center mb-8">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
                            <FaUser className="text-primary text-2xl" />
                        </div>
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">
                            Connexion Admin
                        </h1>
                        <p className="text-gray-600 text-sm">
                            Content de vous revoir ! Veuillez entrer vos identifiants
                        </p>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleLogin} className="space-y-6">
                        {/* Email/Username Input */}
                        <div className="space-y-2">
                            <label 
                                htmlFor="email" 
                                className="block text-sm font-medium text-gray-700"
                            >
                                Email ou Username
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <FaUser className="text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    id="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Entrez vos credentials"
                                    required
                                    className="block w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                                />
                            </div>
                        </div>

                        {/* Password Input */}
                        <div className="space-y-2">
                            <label 
                                htmlFor="password" 
                                className="block text-sm font-medium text-gray-700"
                            >
                                Mot de passe
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <FaLock className="text-gray-400" />
                                </div>
                                <input
                                    type="password"
                                    id="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Entrez votre mot de passe"
                                    required
                                    className="block w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                                />
                            </div>
                        </div>

                        {/* Submit Button */}
                        <button 
                            type="submit" 
                            disabled={loading} 
                            className="w-full bg-primary hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <FaSpinner className="animate-spin" />
                                    <span>Connexion...</span>
                                </>
                            ) : (
                                'Se connecter'
                            )}
                        </button>

                        {/* Forgot Password Link */}
                        <div className="text-center">
                            <a 
                                href="/admin/forgot-password" 
                                className="text-sm text-primary hover:text-blue-700 hover:underline transition-colors"
                            >
                                Mot de passe oublié?
                            </a>
                        </div>
                    </form>
                </div>

                {/* Footer Text */}
                <p className="text-center text-sm text-gray-500 mt-6">
                    © 2024 Fournitest. Tous droits réservés.
                </p>
            </div>

            <ToastContainer position="bottom-right" autoClose={3000} />
        </div>
    );
};

export default Authentication;